import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';

const TeacherDashboard = () => {
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    course: '',
    meetingLink: '',
    scheduledAt: '',
    duration: 60
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, coursesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/classes/teacher/my-classes`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/courses`)
      ]);
      setClasses(classesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/classes`, formData);
      setShowModal(false);
      setFormData({ title: '', course: '', meetingLink: '', scheduledAt: '', duration: 60 });
      fetchData();
      alert('Class created successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create class');
    }
  };

  const viewEnrolledStudents = async (courseId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/enrollments/course/${courseId}`);
      const students = response.data.map(e => e.student.name).join(', ');
      alert(`Enrolled Students:\n${students || 'No students enrolled yet'}`);
    } catch (error) {
      alert('Failed to fetch students');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-extrabold mb-2">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                👨‍🏫 Teacher Dashboard
              </span>
            </h1>
            <p className="text-gray-600 font-medium">Manage your classes and students efficiently</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-xl font-bold hover:from-indigo-600 hover:to-purple-600 shadow-xl transform hover:-translate-y-1 transition-all"
          >
            ➕ Create New Class
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 font-semibold">Total Classes</p>
                <p className="text-4xl font-extrabold mt-2">{classes.length}</p>
              </div>
              <div className="text-6xl opacity-50">📅</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 font-semibold">Active Courses</p>
                <p className="text-4xl font-extrabold mt-2">{courses.length}</p>
              </div>
              <div className="text-6xl opacity-50">📚</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 font-semibold">Upcoming</p>
                <p className="text-4xl font-extrabold mt-2">{classes.filter(c => c.status === 'scheduled').length}</p>
              </div>
              <div className="text-6xl opacity-50">⏰</div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 mb-8 rounded-xl shadow-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-extrabold text-blue-900 mb-2">🔒 Security Tip: Enable Zoom Waiting Room</h3>
              <div className="text-sm text-blue-800 font-medium">
                <p className="mb-2">To prevent unauthorized access to your classes:</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>When creating Zoom meetings, enable "Waiting Room"</li>
                  <li>Start meetings 5 minutes early</li>
                  <li>Manually admit only enrolled students</li>
                  <li>Reject unknown participants</li>
                </ol>
                <p className="mt-3">
                  <a 
                    href="https://support.zoom.us/hc/en-us/articles/115000332726-Waiting-Room" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-bold underline text-blue-600 hover:text-blue-800"
                  >
                    Learn how to enable Waiting Room →
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Classes List */}
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">📋 Your Classes</h2>
          <div className="space-y-6">
            {classes.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center border-4 border-gray-200">
                <div className="text-6xl mb-4">📚</div>
                <p className="text-gray-600 text-lg font-medium">No classes created yet. Create your first class!</p>
              </div>
            ) : (
              classes.map(classItem => (
                <div key={classItem._id} className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-indigo-500 hover:shadow-xl transition-all">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-2xl font-extrabold text-gray-900">{classItem.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          classItem.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                          classItem.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {classItem.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <p className="text-gray-700 font-medium">
                          <span className="text-indigo-600 font-bold">📚 Course:</span> {classItem.course.title}
                        </p>
                        <p className="text-gray-700 font-medium">
                          <span className="text-indigo-600 font-bold">📅 Scheduled:</span> {new Date(classItem.scheduledAt).toLocaleString('en-IN', { 
                            dateStyle: 'full', 
                            timeStyle: 'short' 
                          })}
                        </p>
                        <p className="text-gray-700 font-medium">
                          <span className="text-indigo-600 font-bold">⏱️ Duration:</span> {classItem.duration} minutes
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => viewEnrolledStudents(classItem.course._id)}
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl font-bold hover:from-blue-600 hover:to-indigo-600 shadow-lg transform hover:-translate-y-1 transition-all"
                    >
                      👥 View Students
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Create Class Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Create New Class</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">Class Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Course</label>
                  <select
                    value={formData.course}
                    onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>{course.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Meeting Link (Zoom/Google Meet)</label>
                  <input
                    type="url"
                    value={formData.meetingLink}
                    onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="https://zoom.us/j/..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Scheduled Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledAt}
                    onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="flex-1 bg-primary text-white py-2 rounded-lg hover:bg-secondary"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;
